/**
 * CtaActionPicker — small CMS control that lets the admin choose whether a
 * CTA button starts the onboarding/signup flow OR smooth-scrolls the visitor
 * to a designated landing-page section.
 *
 * Sets up to three fields on the parent object via the `onChange` patcher:
 *   action_type:    'signup' | 'scroll'
 *   scroll_target:  id of the target landing section (e.g. 'pricing')
 *   prefill_weeks:  (optional, pricing-only) starting slider value 1..38.
 *                   Empty / unset = use the slider's own default (4 weeks).
 */
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Section ids match the `data-section` / `id` attribute applied to each
// landing-page section so the scroll lookup is a single querySelector call.
export const SCROLL_TARGETS = [
  { value: 'hero',           label: 'Hero (top of page)' },
  { value: 'features',       label: 'Features' },
  { value: 'hero2',          label: 'Hero 2 (Built for Match Day)' },
  { value: 'voice_features', label: 'Voice Features' },
  { value: 'pricing',        label: 'Pricing' },
  { value: 'testimonials',   label: 'Testimonials' },
  { value: 'gallery',        label: 'Gallery' },
  { value: 'faq',            label: 'FAQ' },
  { value: 'about',          label: 'About' },
  { value: 'contact',        label: 'Contact' },
];

// Prefill options for the Pricing Slider. Match the tier breakpoints so the
// label aligns with what the visitor sees once they land on the slider.
// 'default' is the sentinel for "no prefill" (Radix Select forbids empty
// strings on SelectItem values).
export const PRICING_PREFILL_OPTIONS = [
  { value: 'default', label: 'Default (4 weeks — no prefill)' },
  { value: '1',  label: '1 week — Try it free / Weekly' },
  { value: '4',  label: '4 weeks — Monthly' },
  { value: '12', label: '12 weeks — Quarter season' },
  { value: '24', label: '24 weeks — Half season' },
  { value: '38', label: '38 weeks — Full season' },
];

export function CtaActionPicker({ value, onChange, testIdPrefix = 'cta' }) {
  const actionType = value?.action_type === 'scroll' ? 'scroll' : 'signup';
  const scrollTarget = value?.scroll_target || 'pricing';
  const prefillWeeks = value?.prefill_weeks != null ? String(value.prefill_weeks) : 'default';

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div>
        <Label className="text-xs font-semibold">Button Action</Label>
        <p className="text-[11px] text-gray-500 mb-2">Decide what happens when a visitor taps this CTA.</p>
        <div className="inline-flex rounded-full bg-white border border-gray-300 p-1" data-testid={`${testIdPrefix}-action-type-toggle`}>
          <button
            type="button"
            onClick={() => onChange({ action_type: 'signup' })}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              actionType === 'signup' ? 'bg-[#007a33] text-white shadow' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`${testIdPrefix}-action-signup-btn`}
          >
            Start onboarding
          </button>
          <button
            type="button"
            onClick={() => onChange({ action_type: 'scroll', scroll_target: value?.scroll_target || 'pricing' })}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              actionType === 'scroll' ? 'bg-amber-500 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`${testIdPrefix}-action-scroll-btn`}
          >
            Scroll to section
          </button>
        </div>
      </div>

      {actionType === 'scroll' && (
        <div>
          <Label className="text-xs">Scroll target section</Label>
          <Select value={scrollTarget} onValueChange={(v) => onChange({ scroll_target: v })}>
            <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-scroll-target-select`}>
              <SelectValue placeholder="Pick a section" />
            </SelectTrigger>
            <SelectContent>
              {SCROLL_TARGETS.map(t => (
                <SelectItem key={t.value} value={t.value} data-testid={`${testIdPrefix}-scroll-target-${t.value}`}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-gray-500 mt-1">
            Tapping the CTA smooth-scrolls the visitor to this section. Visitors who land here can still click any signup button further down the page.
          </p>

          {scrollTarget === 'pricing' && (
            <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`${testIdPrefix}-prefill-block`}>
              <Label className="text-xs">Pricing slider starting value</Label>
              <Select
                value={prefillWeeks}
                onValueChange={(v) => onChange({ prefill_weeks: v === 'default' ? null : Number(v) })}
              >
                <SelectTrigger className="mt-1" data-testid={`${testIdPrefix}-prefill-weeks-select`}>
                  <SelectValue placeholder="Default (4 weeks — no prefill)" />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_PREFILL_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} data-testid={`${testIdPrefix}-prefill-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-500 mt-1">
                Pre-positions the slider so a CTA like <em>"Try 1 week"</em> lands the visitor on the matching tier. Leave on default for general "See pricing" CTAs.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CtaActionPicker;
